import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import { tryReadAdminV2RuntimePath } from './runtime-path';

export type AdminV2MediaMetaSource =
  | 'clientProvidedMeta'
  | 'providerReturnedMeta';

export type AdminV2MediaMetaContext = {
  clientProvidedMeta?: Record<string, unknown>;
  providerReturnedMeta?: Record<string, unknown>;
};

export function readAdminV2MediaMetaValue(params: {
  context?: AdminV2MediaMetaContext;
  fieldId: string;
  metaSource: AdminV2MediaMetaSource;
  metaPath: string[];
}) {
  const source = params.context?.[params.metaSource];
  const fieldMeta = source?.[params.fieldId];
  if (params.metaPath.length === 1 && params.metaPath[0] === '*') {
    return fieldMeta;
  }
  const result = tryReadAdminV2RuntimePath({
    value: fieldMeta,
    path: params.metaPath,
    label: `media metadata ${params.metaSource}.${params.fieldId}`,
  });
  if (result.ok) {
    return result.value;
  }
  if (result.reason === 'missing') {
    return undefined;
  }
  throw createAdminV2Error({
    code: 'ADMIN_V2_MEDIA_META_PATH_INVALID',
    message: result.message,
    fieldPath: `mediaMeta.${params.metaSource}.${params.fieldId}.${params.metaPath.join('.')}`,
    status: 422,
    details: {
      segment: result.segment,
      path: params.metaPath,
    },
  });
}

export function mergeAdminV2MediaMetaContext(
  ...contexts: Array<AdminV2MediaMetaContext | undefined>
): AdminV2MediaMetaContext {
  return contexts.reduce<AdminV2MediaMetaContext>((merged, context) => {
    if (!context) return merged;
    return {
      ...(merged.clientProvidedMeta || context.clientProvidedMeta
        ? {
            clientProvidedMeta: {
              ...(merged.clientProvidedMeta ?? {}),
              ...(context.clientProvidedMeta ?? {}),
            },
          }
        : {}),
      ...(merged.providerReturnedMeta || context.providerReturnedMeta
        ? {
            providerReturnedMeta: {
              ...(merged.providerReturnedMeta ?? {}),
              ...(context.providerReturnedMeta ?? {}),
            },
          }
        : {}),
    };
  }, {});
}
